"use client";
import Link from "next/link";
import { ArrowRight, Mail, MessageSquare, Phone, Users } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { contactSupport as data } from "@/lib/data/support";

const accent = "var(--color-cyan)";
const chIcons: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = { "Support Portal": MessageSquare, "Email": Mail, "Phone": Phone, "Dedicated Slack/Teams": MessageSquare, "Emergency Hotline": Phone, "Account Manager": Users };

export function ContactSupport() {
  return (
    <Section id="contact-support" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-cyan)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-10 glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgb(var(--border)/0.18)] bg-[rgb(var(--border)/0.04)]">
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Channel</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden md:table-cell">Availability</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Best For</th>
                  </tr>
                </thead>
                <tbody>
                  {data.channels.map((c) => {
                    const Icon = chIcons[c.channel] ?? MessageSquare;
                    return (
                      <tr key={c.channel} className="border-b border-[rgb(var(--border)/0.08)]">
                        <td className="px-4 py-3 align-top">
                          <div className="flex items-center gap-2">
                            <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: accent }} />
                            <span className="font-medium">{c.channel}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top hidden md:table-cell text-[rgb(var(--fg-muted))]">{c.availability}</td>
                        <td className="px-4 py-3 align-top text-[rgb(var(--fg-muted))]">{c.bestFor}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>
        <Reveal delay={200}>
          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            <div className="glass rounded-2xl p-6 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <span className="eyebrow-tiny font-bold" style={{ color: accent }}>Contact Information</span>
              <div className="mt-4 space-y-2">
                {data.contacts.map((c) => (
                  <div key={c.purpose} className="text-sm">
                    <span className="font-semibold">{c.purpose}:</span> <a href={`mailto:${c.contact}`} className="underline-offset-2 hover:underline" style={{ color: accent }}>{c.contact}</a>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass rounded-2xl p-6 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <span className="eyebrow-tiny font-bold" style={{ color: accent }}>Response Commitments</span>
              <div className="mt-4 space-y-2">
                {data.commitments.map((c) => (
                  <div key={c.tier} className="text-sm">
                    <span className="font-semibold" style={{ color: accent }}>{c.tier}:</span> <span className="text-[rgb(var(--fg-muted))]">{c.response} · {c.coverage}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={500}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link key={c.label} href={c.href} className={i === 0 ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm" : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"}>
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
